import { LockOutlined, MobileOutlined, UserOutlined } from "@ant-design/icons";
import { Button, Form, Input, message } from "antd";
import axios from "axios";
// NOTE: same import as the jewel admin login. If the retail project has
// "framer-motion" in package.json instead of "motion", change this line to:
// import { AnimatePresence, motion } from "framer-motion";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
// ASSUMPTION: retail Config exports CREATE_jwel (main API) and CREATE_jwel1
// (OTP API), same names as the jewel admin Config. Change here if different.
import { CREATE_jwel, CREATE_jwel1 } from "../../Config/Config";
import styles from "./loginpage.module.css";

const SESSION_FLAG = "activeSession";

/* ─────────────────────────────────────────────────────────────
   API endpoints — the ONLY place that differs from jewel admin.
   Retail uses /api/ERP/... for table reads (jewel uses /api/Master/...).
   Tenant + OTP endpoints are ASSUMED to be the same paths as jewel;
   edit them here if the retail backend uses different ones.
   ───────────────────────────────────────────────────────────── */
const API = {
  TABLE: `${CREATE_jwel}/api/ERP/GetDataFromGivenTableName`,
  TABLE_WHERE: `${CREATE_jwel}/api/ERP/GetDataFromGivenTableNameWithWhere`,
  CLIENT_DETAILS: `${CREATE_jwel}/api/Tenant/GetAllClientDetails`,
  CLIENT_TABLE_WHERE: `${CREATE_jwel}/api/Tenant/GetDataFromGivenTableNameWithWhere`,
  VALID_TENANT: `${CREATE_jwel}/api/Tenant/CheckValidTenant`,
  OTP_SEND: `${CREATE_jwel1}/api/WebOtp/Send`,
  OTP_VERIFY: `${CREATE_jwel1}/api/WebOtp/Verify`,
};

// Jewel admin blocks users who lack DELIVERYCHARGES = 1 (unless ADMIN).
// ASSUMPTION: retail users don't have that flag, so the gate is OFF here.
// Set to true if retail should enforce the same permission check.
const REQUIRE_LOGIN_PERMISSION = false;

// These roles skip the TSSERP_PERMISSIONS lookup (full access).
const BYPASS_ROLES = ["ADMIN", "MAINADMIN", "ORIGIN", "SUPERADMIN"];

// Escape single quotes so a username like O'Brien can't break the where clause.
const sqlEscape = (value) => String(value ?? "").replace(/'/g, "''");

export const clearAuthStorage = () => {
  localStorage.clear();
  sessionStorage.removeItem(SESSION_FLAG);
};

const STAGE_COPY = {
  password: {
    heading: "Log in",
    subheading: "Enter your credentials to continue",
  },
  mobile: {
    heading: "Log in",
    subheading: "We'll send a one-time code to verify it's you",
  },
  otp: {
    heading: "Log in",
    subheading: "Enter the 6-digit code we sent you",
  },
};

const stageVariants = {
  initial: { opacity: 0, x: 24 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -24 },
};

const Divider = () => (
  <div className={styles.divider}>
    <span className={styles.dividerLine} />
    <span className={styles.dividerText}>OR</span>
    <span className={styles.dividerLine} />
  </div>
);

const LoginPage = ({ onLogin }) => {
  const navigate = useNavigate();
  console.log("CREATE_jwel:", CREATE_jwel, "| CREATE_jwel1:", CREATE_jwel1);

  const [loading, setLoading] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);

  const [stage, setStage] = useState("password");
  const [resendTimer, setResendTimer] = useState(0);
  const [otpForm] = Form.useForm();

  // Autofill prevention (kept from the existing retail login):
  // random field names + readOnly until the user focuses the field.
  const fieldSuffix = useRef(Math.random().toString(36).slice(2, 8)).current;
  const [usernameLocked, setUsernameLocked] = useState(true);
  const [passwordLocked, setPasswordLocked] = useState(true);

  const usernameRef = useRef(null);
  const mobileRef = useRef(null);
  const otpRef = useRef(null);
  const otpGroupRef = useRef(null);

  /* ── Resend countdown ── */
  useEffect(() => {
    if (resendTimer <= 0) {
      return;
    }

    const timer = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }

        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [resendTimer]);

  /* ── Session check on mount ── */
  useEffect(() => {
    const hasTenant = !!localStorage.getItem("tenantName");
    const hasActiveSession = sessionStorage.getItem(SESSION_FLAG) === "1";

    if (hasTenant && hasActiveSession) {
      // Same tab, just refreshed/navigated — still logged in.
      navigate("/dashboard");
      return;
    }

    // Tab was closed & reopened (localStorage survived, sessionStorage
    // didn't) — force a fresh login.
    if (hasTenant && !hasActiveSession) {
      clearAuthStorage();
    }

    navigate("/");
  }, [navigate]);

  /* ─────────────────────────────────────────────
     Data loaders — run after login succeeds.
     They keep the SAME localStorage keys the retail app already reads.
     ───────────────────────────────────────────── */

  // Firm details (FIRM_CONFIGURE)
  const userAPI = async (tenantName) => {
    try {
      const response = await axios.get(API.TABLE, {
        params: { tableName: "FIRM_CONFIGURE" },
        headers: { tenantName },
      });

      const data = response?.data;

      if (Array.isArray(data) && data.length > 0) {
        const firm = data[0];

        // Stored as a comma-joined string, exactly like the old retail login.
        const imageUrls = firm?.EPASS1
          ? String(firm.EPASS1)
            .split(",")
            .map((item) => item.trim())
            .filter(Boolean)
          : [];

        localStorage.setItem("images", imageUrls.join(","));
        localStorage.setItem("user", firm?.FIRMNAME || "");
        localStorage.setItem("city", firm?.CITY || "");
        localStorage.setItem("singleImage", firm?.EPASS2 || "");
        localStorage.setItem("gstValue", firm?.GST || "");
      }
    } catch (error) {
      console.error("User API error:", error);
    }
  };

  // Login user profile (LOGINUSER_PROFILE)
  const userConditionAPI = async (username, tenantName) => {
    try {
      const response = await axios.get(API.TABLE_WHERE, {
        params: {
          tableName: "LOGINUSER_PROFILE",
          where: `USERNAME='${sqlEscape(username)}'`,
        },
        headers: { tenantName },
      });

      const data = response?.data;

      if (Array.isArray(data) && data.length > 0) {
        const user = data[0];

        localStorage.setItem("userType", user?.USER_TYPE || "");
        localStorage.setItem("ipAddress", user?.PRINTERIP || "");
        localStorage.setItem("printModel", user?.PRINT_MODEL || "");
        localStorage.setItem("pdfModule", user?.PRINTQUALITY || "");
        localStorage.setItem("loginName", user?.USERNAME || username);
        localStorage.setItem("wastMc", user?.WAST_MC || "");
        localStorage.setItem("upiId", user?.SOFT_CALCMCWITH || "");
        localStorage.setItem("jewel", user?.INVOICE_GEN_MODEL || "");
      }
    } catch (error) {
      console.error("User condition API error:", error);
    }
  };

  // Role + module, used when the login response didn't include them
  const getUserRole = async (userName) => {
    try {
      const response = await axios.get(API.CLIENT_TABLE_WHERE, {
        params: {
          tableName: "ClientDetails",
          where: `USERNAME='${sqlEscape(userName)}'`,
        },
        headers: { tenantName: userName },
      });

      const data = Array.isArray(response?.data)
        ? response.data[0]
        : response?.data;

      return {
        role: data?.ROLE || "",
        userModule: data?.USERMODULE || "",
      };
    } catch (error) {
      console.error("Get user role error:", error);

      return { role: "", userModule: "" };
    }
  };

  // Per-user screen permissions (skipped for admin-type roles)
  const fetchAndCachePermissions = async (userName, tenantName, role) => {
    if (BYPASS_ROLES.includes((role || "").toUpperCase())) {
      return;
    }

    try {
      const res = await axios.get(API.TABLE, {
        params: { tableName: "TSSERP_PERMISSIONS" },
        headers: { tenantName },
      });

      const data = Array.isArray(res.data) ? res.data : [];

      const row = data.find(
        (r) => r.USERNAME?.toUpperCase() === userName?.toUpperCase(),
      );

      if (row) {
        localStorage.setItem("userPermissions", JSON.stringify(row));
      }
    } catch (err) {
      console.error("Failed to cache user permissions:", err);
    }
  };

  /* ─────────────────────────────────────────────
     Shared final step — used by BOTH password and OTP login.
     ───────────────────────────────────────────── */
  const completeLogin = async ({ tenantName, userName, role, userModule }) => {
    if (!tenantName || !userName) {
      message.error("Login information is incomplete.");

      return false;
    }

    localStorage.setItem("isLoggedIn", "true");
    localStorage.setItem("tenantName", tenantName);
    localStorage.setItem("UName", userName);
    // Fallback; overwritten by LOGINUSER_PROFILE.USERNAME if that row exists.
    localStorage.setItem("loginName", userName);

    let finalRole = role;
    let finalUserModule = userModule;

    if (!finalRole) {
      const resolved = await getUserRole(userName);

      finalRole = resolved.role;

      if (!finalUserModule) {
        finalUserModule = resolved.userModule;
      }
    }

    if (finalRole) {
      localStorage.setItem("role", finalRole);
      localStorage.setItem("ROLE", finalRole);
      // The old retail login stored the role as "adminType"; keep it working.
      localStorage.setItem("adminType", finalRole);
    }

    localStorage.setItem("userModule", (finalUserModule || "").toUpperCase());

    sessionStorage.setItem(SESSION_FLAG, "1");

    localStorage.removeItem("userPermissions");

    // Everything is loaded BEFORE navigating, so the old
    // window.location.reload() calls are no longer needed.
    await Promise.allSettled([
      userAPI(tenantName),
      userConditionAPI(userName, tenantName),
      fetchAndCachePermissions(userName, tenantName, finalRole),
    ]);

    if (typeof onLogin === "function") {
      onLogin(tenantName);
    }

    message.success("Login successful");

    navigate("/dashboard");

    return true;
  };

  /* ─────────────────────────────────────────────
     Password login
     ───────────────────────────────────────────── */
  const onFinish = async (values) => {
    const username = values?.username?.trim();
    const password = values?.password;

    if (!username || !password) {
      message.error("Please enter username and password.");

      return;
    }

    setLoading(true);

    try {
      // Client record gives us ROLE + USERMODULE up front.
      const clientRecord = await axios
        .get(API.CLIENT_DETAILS, {
          params: { userName: username, password },
        })
        .then((res) => (Array.isArray(res?.data) ? res.data[0] : res?.data))
        .catch(() => null);

      if (REQUIRE_LOGIN_PERMISSION) {
        if (!clientRecord) {
          message.error("Invalid username or password.");

          return;
        }

        const hasDeliveryCharges =
          String(clientRecord?.DELIVERYCHARGES) === "1";

        const isAdmin =
          String(clientRecord?.ROLE || "").toUpperCase() === "ADMIN";

        if (!hasDeliveryCharges && !isAdmin) {
          message.error("You don't have permission to login.");

          return;
        }
      }

      const response = await axios.get(API.VALID_TENANT, {
        params: { userName: username, password },
      });

      const tenantName = response?.data;

      if (!tenantName) {
        message.error("Invalid username or password.");

        return;
      }

      await completeLogin({
        tenantName,
        userName: username,
        role: clientRecord?.ROLE,
        userModule: clientRecord?.USERMODULE || "",
      });
    } catch (error) {
      console.error("Password login error:", error);

      message.error(
        error?.response?.data?.message ||
        error?.message ||
        "Login failed. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  /* ─────────────────────────────────────────────
     OTP login
     ───────────────────────────────────────────── */
  const handleSendOtp = async () => {
    try {
      if (resendTimer > 0) {
        message.info(
          `Please wait ${resendTimer} seconds before requesting another OTP.`,
        );

        return;
      }

      if (sendingOtp) {
        return;
      }

      const values = await otpForm.validateFields(["mobile"]);

      const mobile = String(values?.mobile || "").trim();

      if (!mobile) {
        message.error("Please enter your mobile number.");

        return;
      }

      if (!/^[0-9]{10}$/.test(mobile)) {
        message.error("Please enter a valid 10-digit mobile number.");

        return;
      }

      setSendingOtp(true);

      const response = await axios.post(
        API.OTP_SEND,
        { loginMobileNo: mobile },
        {
          headers: { "Content-Type": "application/json" },
          timeout: 30000,
        },
      );

      const data = response?.data;

      if (data?.success === true) {
        setResendTimer(60);

        otpForm.resetFields(["otp"]);

        setStage("otp");

        message.success(`OTP sent successfully to ${mobile} via WhatsApp.`);
      } else {
        message.error(data?.message || "Failed to send OTP. Please try again.");
      }
    } catch (error) {
      console.error("Send OTP error:", error);

      // antd validation error — the field already shows its own message.
      if (error?.errorFields) {
        return;
      }

      if (error?.response) {
        message.error(
          error?.response?.data?.message ||
          `OTP API failed (${error.response.status})`,
        );
      } else if (error?.request) {
        message.error(
          "Unable to connect to OTP server. Please check your API URL or network.",
        );
      } else {
        message.error(
          error?.message || "Failed to send OTP. Please try again.",
        );
      }
    } finally {
      setSendingOtp(false);
    }
  };

  const handleVerifyOtp = async (values) => {
    const mobile = String(values?.mobile || "").trim();
    const otp = String(values?.otp || "").trim();

    if (!mobile) {
      message.error("Mobile number is required.");

      return;
    }

    if (!/^[0-9]{10}$/.test(mobile)) {
      message.error("Invalid mobile number.");

      return;
    }

    if (!otp || otp.length !== 6) {
      message.error("Please enter the complete 6-digit OTP.");

      return;
    }

    setVerifyingOtp(true);

    try {
      const response = await axios.post(
        API.OTP_VERIFY,
        { loginMobileNo: mobile, otpCode: otp },
        {
          headers: { "Content-Type": "application/json" },
          timeout: 30000,
        },
      );

      const data = response?.data;

      if (data?.success !== true) {
        message.error(data?.message || "Invalid OTP.");

        return;
      }

      // Same response shape as jewel admin (with casing fallbacks).
      const tenantName =
        data?.tenantName ||
        data?.TenantName ||
        data?.tenant ||
        data?.TENANTNAME;

      const userName =
        data?.userName ||
        data?.UserName ||
        data?.username ||
        data?.USERNAME ||
        mobile;

      const role = data?.role || data?.Role || data?.ROLE || "";

      if (!tenantName) {
        console.error(
          "OTP verification succeeded but tenantName is missing:",
          data,
        );

        message.error(
          "OTP verified, but tenant information was not returned by the server.",
        );

        return;
      }

      await completeLogin({
        tenantName,
        userName,
        role,
        userModule: data?.USERMODULE || data?.userModule || "",
      });
    } catch (error) {
      console.error("Verify OTP error:", error);

      if (error?.response) {
        message.error(
          error?.response?.data?.message ||
          `OTP verification failed (${error.response.status})`,
        );
      } else if (error?.request) {
        message.error("Unable to connect to OTP server. Please try again.");
      } else {
        message.error(
          error?.message || "OTP verification failed. Please try again.",
        );
      }
    } finally {
      setVerifyingOtp(false);
    }
  };

  /* ─────────────────────────────────────────────
     Keyboard handling
     ───────────────────────────────────────────── */

  // Enter moves username → password → Log in button (password stage only).
  const handleKeyDown = (e) => {
    if (e.key !== "Enter") {
      return;
    }

    // Skip the hidden decoy inputs (tabIndex -1) and disabled controls.
    const formElements = Array.from(
      document.querySelectorAll(
        'input:not([disabled]):not([tabindex="-1"]), button:not([disabled])',
      ),
    );

    const index = formElements.indexOf(e.target);

    if (index > -1 && index < formElements.length - 1) {
      e.preventDefault();

      formElements[index + 1]?.focus();
    }
  };

  // Enter inside the OTP boxes: next box, or submit on the last one.
  const handleOtpKeyDown = (e) => {
    if (e.key !== "Enter") {
      return;
    }

    e.preventDefault();

    const inputs = Array.from(
      otpGroupRef.current?.querySelectorAll("input") || [],
    );

    const currentIndex = inputs.indexOf(e.target);

    if (currentIndex === -1) {
      return;
    }

    if (currentIndex < inputs.length - 1) {
      inputs[currentIndex + 1]?.focus();

      return;
    }

    otpForm.submit();
  };

  /* ─────────────────────────────────────────────
     Stage focus + navigation
     ───────────────────────────────────────────── */
  useEffect(() => {
    const timer = setTimeout(() => {
      usernameRef.current?.focus();
    }, 120);

    return () => clearTimeout(timer);
  }, []);

  const focusStageField = (enteredStage) => {
    if (enteredStage === "password") {
      usernameRef.current?.focus();
    } else if (enteredStage === "mobile") {
      mobileRef.current?.focus();
    } else if (enteredStage === "otp") {
      requestAnimationFrame(() => {
        const groupEl = otpGroupRef.current;

        if (!groupEl) {
          return;
        }

        if (!groupEl.contains(document.activeElement)) {
          groupEl.querySelector("input")?.focus();
        }
      });
    }
  };

  const handleStageAnimationComplete = (enteredStage) => (definition) => {
    if (definition === "animate") {
      focusStageField(enteredStage);
    }
  };

  const goToOtpMobileStage = () => {
    otpForm.resetFields(["mobile", "otp"]);
    setResendTimer(0);
    setStage("mobile");
  };

  const goToPasswordStage = () => {
    otpForm.resetFields(["mobile", "otp"]);
    setResendTimer(0);
    // Re-lock so the autofill guard works again on the fresh inputs.
    setUsernameLocked(true);
    setPasswordLocked(true);
    setStage("password");
  };

  const goBackToMobileStage = () => {
    otpForm.resetFields(["otp"]);
    setStage("mobile");
  };

  /* ─────────────────────────────────────────────
     Render
     ───────────────────────────────────────────── */
  return (
    <div className={styles.background}>
      <motion.div
        layout
        className={styles.loginContainer}
        transition={{ layout: { duration: 0.35, ease: "easeInOut" } }}
      >
        <div className={styles.headerBlock}>
          <h2 className={styles.loginHeader}>{STAGE_COPY[stage].heading}</h2>
          <p className={styles.loginSubheader}>
            {STAGE_COPY[stage].subheading}
          </p>
        </div>

        <AnimatePresence mode="wait" initial={false}>
          {/* ── Stage 1: username + password ── */}
          {stage === "password" && (
            <motion.div
              key="password"
              variants={stageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{ duration: 0.25, ease: "easeOut" }}
              onAnimationComplete={handleStageAnimationComplete("password")}
            >
              <Form
                name="login"
                onFinish={onFinish}
                layout="vertical"
                autoComplete="off"
              >
                {/* Decoy fields: invisible, but the first username/password-
                    shaped inputs Chrome's autofill finds, so it fills these
                    instead of the real ones below. */}
                <div
                  style={{
                    position: "absolute",
                    width: 0,
                    height: 0,
                    overflow: "hidden",
                    opacity: 0,
                    pointerEvents: "none",
                  }}
                  aria-hidden="true"
                >
                  <input
                    type="text"
                    name="username"
                    tabIndex={-1}
                    autoComplete="off"
                  />
                  <input
                    type="password"
                    name="password"
                    tabIndex={-1}
                    autoComplete="new-password"
                  />
                </div>

                <Form.Item
                  name="username"
                  label="Username"
                  rules={[
                    { required: true, message: "Please input your username!" },
                  ]}
                >
                  <Input
                    ref={usernameRef}
                    className={styles.customInput}
                    prefix={<UserOutlined style={{ color: "#4FBE91" }} />}
                    placeholder="Username"
                    autoComplete="off"
                    name={`usr_${fieldSuffix}`}
                    readOnly={usernameLocked}
                    onFocus={() => setUsernameLocked(false)}
                    onKeyDown={handleKeyDown}
                  />
                </Form.Item>

                <Form.Item
                  name="password"
                  label="Password"
                  rules={[
                    { required: true, message: "Please input your password!" },
                  ]}
                >
                  <Input.Password
                    className={styles.customInput}
                    prefix={<LockOutlined style={{ color: "#4FBE91" }} />}
                    placeholder="Password"
                    autoComplete="new-password"
                    name={`pwd_${fieldSuffix}`}
                    readOnly={passwordLocked}
                    onFocus={() => setPasswordLocked(false)}
                    onKeyDown={handleKeyDown}
                  />
                </Form.Item>

                <Form.Item style={{ marginBottom: 0 }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    className={styles.loginButton}
                    block
                  >
                    Log in
                  </Button>
                </Form.Item>
              </Form>

              <Divider />

              <Button
                block
                className={styles.secondaryButton}
                icon={<MobileOutlined />}
                onClick={goToOtpMobileStage}
              >
                Log in with Mobile OTP
              </Button>
            </motion.div>
          )}

          {/* ── Stage 2: mobile number ── */}
          {stage === "mobile" && (
            <motion.div
              key="mobile"
              variants={stageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{ duration: 0.25, ease: "easeOut" }}
              onAnimationComplete={handleStageAnimationComplete("mobile")}
            >
              <Form
                form={otpForm}
                name="otp-mobile"
                layout="vertical"
                autoComplete="off"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleSendOtp();
                  }
                }}
              >
                <Form.Item
                  name="mobile"
                  label="Mobile number"
                  rules={[
                    {
                      required: true,
                      message: "Please input your mobile number!",
                    },
                    {
                      pattern: /^[0-9]{10}$/,
                      message: "Enter a valid 10-digit mobile number",
                    },
                  ]}
                >
                  <Input
                    ref={mobileRef}
                    className={styles.customInput}
                    prefix={<MobileOutlined style={{ color: "#4FBE91" }} />}
                    placeholder="Mobile number"
                    maxLength={10}
                    inputMode="numeric"
                    onChange={(e) => {
                      // digits only
                      otpForm.setFieldsValue({
                        mobile: e.target.value.replace(/\D/g, ""),
                      });
                    }}
                  />
                </Form.Item>

                <Form.Item style={{ marginBottom: 0 }}>
                  <Button
                    type="primary"
                    block
                    className={styles.loginButton}
                    loading={sendingOtp}
                    disabled={resendTimer > 0}
                    onClick={handleSendOtp}
                  >
                    {resendTimer > 0
                      ? `Resend OTP in ${resendTimer}s`
                      : "Get OTP"}
                  </Button>
                </Form.Item>
              </Form>

              <Divider />

              <Button
                block
                className={styles.secondaryButton}
                icon={<UserOutlined />}
                onClick={goToPasswordStage}
              >
                Log in with Username & Password
              </Button>
            </motion.div>
          )}

          {/* ── Stage 3: 6-digit OTP ── */}
          {stage === "otp" && (
            <motion.div
              key="otp"
              variants={stageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{ duration: 0.25, ease: "easeOut" }}
              onAnimationComplete={handleStageAnimationComplete("otp")}
            >
              <Form
                form={otpForm}
                name="otp-verify"
                onFinish={handleVerifyOtp}
                layout="vertical"
                autoComplete="off"
              >
                {/* Keeps the mobile number in the form values for verify */}
                <Form.Item name="mobile" hidden>
                  <Input />
                </Form.Item>

                <div ref={otpGroupRef} onKeyDown={handleOtpKeyDown}>
                  <Form.Item
                    name="otp"
                    rules={[
                      { required: true, message: "Please input the OTP!" },
                      {
                        pattern: /^[0-9]{6}$/,
                        message: "Enter the full 6-digit OTP",
                      },
                    ]}
                  >
                    <Input.OTP
                      ref={otpRef}
                      length={6}
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </div>

                <div className={styles.resendRow}>
                  <Button
                    type="link"
                    size="small"
                    onClick={handleSendOtp}
                    loading={sendingOtp}
                    disabled={sendingOtp || resendTimer > 0}
                    className={styles.resendLink}
                  >
                    {resendTimer > 0
                      ? `Resend OTP in ${resendTimer}s`
                      : "Resend OTP"}
                  </Button>
                </div>

                <div className={styles.stageActions}>
                  <Button
                    block
                    className={`${styles.backButton} ${styles.flexItem}`}
                    onClick={goBackToMobileStage}
                  >
                    Back
                  </Button>
                  <Button
                    type="primary"
                    htmlType="submit"
                    block
                    loading={verifyingOtp}
                    className={`${styles.loginButton} ${styles.flexItem}`}
                  >
                    Log in
                  </Button>
                </div>
              </Form>
            </motion.div>
          )}
        </AnimatePresence>
        <div className={styles.footer}>
          <p className={styles.footerText}>
            Powered by TIMESERA SOFTWARE SOLUTIONS PVT LTD
          </p>
          <p className={styles.footerVersion}>TIMESERA ERP v-1.3.1</p>
        </div>
      </motion.div>
    </div>
  );
};

export default LoginPage;